import { Flag } from 'lucide-react';

import type { BetSnapshotProps } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { ChatCard, SnapHead, SparkStatsWell, tierColor, deriveSpark } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { SkBar, SkChip } from './skeletons';

const PURPLE = 'var(--color-purple-400)';

export function BetSnapshotWidget({
  title,
  summary,
  tag = 'Headline bet',
  confidence,
  lvl,
  trend,
  sparkline,
  interviews,
  signals,
  assumptions,
}: BetSnapshotProps) {
  const up = trend >= 0;
  const spark = sparkline && sparkline.length >= 2 ? sparkline : deriveSpark(confidence, trend);

  return (
    <ChatCard className="flex flex-col gap-[13px] p-4 text-sm">
      <SnapHead
        icon={Flag}
        iconColor={PURPLE}
        eyebrow={tag}
        title={title}
        confidence={confidence}
        lvl={lvl}
        trend={trend}
      />

      {summary && (
        <p className="text-[12px] leading-[1.5] text-muted-foreground text-pretty">{capitalizeFirst(summary)}</p>
      )}

      <SparkStatsWell spark={spark} up={up} interviews={interviews} signals={signals} linkLabel="Open bet" />

      {assumptions && assumptions.length > 0 && (
        <div className="flex flex-col gap-[7px]">
          <span className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
            Assumptions · {assumptions.length}
          </span>
          {assumptions.map((a, i) => (
            <div
              key={i}
              className="grid grid-cols-[3px_1fr_auto] items-center gap-2.5 rounded-[9px] border border-border bg-secondary py-2 pl-[9px] pr-[11px]"
            >
              <span
                className="self-stretch rounded-[3px]"
                style={{ background: tierColor(a.lvl) }}
                aria-hidden
              />
              <span className="min-w-0 text-[12px] font-medium leading-[1.35] text-foreground">
                {capitalizeFirst(a.text)}
              </span>
              <span
                className="shrink-0 font-mono text-[11px] font-bold"
                style={{ color: tierColor(a.lvl) }}
              >
                {a.confidence}%
              </span>
            </div>
          ))}
        </div>
      )}
    </ChatCard>
  );
}

export function BetSnapshotSkeleton({ className }: { className?: string }) {
  return (
    <ChatCard className={cn('flex flex-col gap-[13px] p-4', className)}>
      {/* SnapHead */}
      <div className="flex items-start gap-[11px]">
        <SkBar width="30px" height="30px" radius="8px" />
        <div className="flex flex-1 flex-col gap-[5px]">
          <SkBar width="90px" height="8px" />
          <SkBar width="70%" height="13px" />
        </div>
        <div className="flex flex-col items-end gap-[5px]">
          <SkChip width="44px" height="18px" />
          <SkBar width="34px" height="8px" />
        </div>
      </div>
      {/* summary */}
      <SkBar width="92%" height="9px" />
      {/* spark stats well */}
      <div
        className="flex items-center gap-3 rounded-[11px] border border-border px-3 py-2.5"
        style={{ background: 'var(--tinted-bg)' }}
      >
        <SkBar width="116px" height="26px" radius="6px" />
        <div className="flex flex-col gap-1.5">
          <SkBar width="92px" height="9px" />
          <SkBar width="80px" height="9px" />
        </div>
        <SkBar width="28px" height="28px" radius="6px" className="ml-auto" />
      </div>
      {/* assumptions */}
      <SkBar width="90px" height="8px" />
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="grid grid-cols-[3px_1fr_auto] items-center gap-2.5 rounded-[9px] border border-border bg-secondary px-[11px] py-2.5"
        >
          <span className="h-full self-stretch rounded-[3px] bg-muted" />
          <SkBar width={`${70 - i * 6}%`} height="9px" />
          <SkChip width="36px" height="16px" />
        </div>
      ))}
    </ChatCard>
  );
}
